import {
  FC,
  useCallback,
  HTMLAttributes,
  useRef,
  createElement,
  useState
} from 'react';

interface OnVisibleProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Triggered when an element becomes visible
   */
  onChange?: (visible: boolean) => void;
  /**
   * Percentage of element that should be visible before triggering change.
   * e.g `percent={80}`
   */
  percent?: number;
  /**
   * class name that should be applied when visible
   */
  visibleClassName?: string;
  /**
   * Element of the wrapper that should be used
   * E.g: `span`
   */
  wrappingElement?: string;
}

const OnVisible: FC<OnVisibleProps> = (props: OnVisibleProps) => {
  const {
    onChange,
    children,
    percent = 50,
    visibleClassName,
    wrappingElement = 'div',
    ...rest
  } = props;

  const [visible, setVisible] = useState(false);

  const intersectionObserverRef = useRef<IntersectionObserver>(null);

  const handleInteraction = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        onChange?.(true);
        setVisible(true);
        intersectionObserverRef?.current?.disconnect();
      }
    });
  };

  const domRef = useCallback((ref: HTMLDivElement) => {
    const intersectionObserver = new IntersectionObserver(handleInteraction, {
      threshold: percent / 100
    });
    intersectionObserverRef.current = intersectionObserver;
    if (ref) intersectionObserver.observe(ref);
    return () => intersectionObserver.disconnect();
  }, []);

  return createElement(
    wrappingElement,
    {
      ref: domRef,
      ...rest,
      className: visible ? visibleClassName : undefined
    },
    children
  );
};

export default OnVisible;
